import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useConversationMessages } from '../../../hooks/useConversationMessages';
import { useRealtimeSubscription } from '../../../hooks/useRealtimeSubscription';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ChatService from '../../../services/chatService';
import EscalateToTicketModal from '../../../components/admin/EscalateToTicketModal';
import supabase from '../../../utils/supabase';

/**
 * Moderator Chat Dashboard
 * 
 * Features:
 * - Moderators can see open/unclaimed conversations
 * - Once a moderator responds, the conversation is auto-claimed (exclusively theirs)
 * - Only the assigned moderator can update status or escalate
 * - Split view: "Available" vs "My Conversations"
 */
const ModeratorChatDashboard = () => {
  const { userProfile } = useAuth();
  
  // Conversation state
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [viewMode, setViewMode] = useState('available'); // 'available' | 'my'
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [uiError, setUiError] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // Stable callback for messages loaded
  const handleMessagesLoaded = useCallback((count) => {
    console.log('[ModeratorChat] Messages loaded:', count);
  }, []);
  
  // Conversation messages hook
  const {
    messages,
    loading: messagesLoading,
    error: messagesError,
    isAtBottom,
    loadMessages,
    sendMessage,
    handleScroll: handleMessageScroll
  } = useConversationMessages(selectedConversation?.id, handleMessagesLoaded);

  // Memoized color mappings
  const statusColors = useMemo(() => ({
    open: 'text-red-600 bg-red-50 border-red-200',
    in_progress: 'text-blue-600 bg-blue-50 border-blue-200',
    resolved: 'text-green-600 bg-green-50 border-green-200',
    closed: 'text-gray-600 bg-gray-50 border-gray-200'
  }), []);

  const priorityColors = useMemo(() => ({
    low: 'text-gray-600 bg-gray-50',
    normal: 'text-blue-600 bg-blue-50',
    high: 'text-orange-600 bg-orange-50',
    urgent: 'text-red-600 bg-red-50'
  }), []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!userProfile?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('[ModeratorChat] Loading conversations for moderator:', userProfile.id);
      
      // Fetch conversations - RLS will filter to unclaimed + moderator's claimed
      const { data, error: fetchError } = await supabase
        .from('chat_conversations')
        .select(`
          *,
          user:user_id(id, full_name, profile_picture, email),
          claimed_moderator:claimed_by(id, full_name)
        `)
        .neq('is_archived', true)
        .order('updated_at', { ascending: false });
      
      if (fetchError) throw fetchError;
      
      console.log('[ModeratorChat] Loaded conversations:', data?.length);
      setConversations(data || []);
    } catch (err) {
      console.error('[ModeratorChat] Failed to load conversations:', err);
      setError(err.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [userProfile?.id]);

  // Initial load
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Load messages when conversation changes
  useEffect(() => {
    if (selectedConversation?.id) {
      loadMessages();
    }
  }, [selectedConversation?.id, loadMessages]);

  // Filter conversations based on view mode
  const filteredConversations = useMemo(() => {
    if (viewMode === 'my') {
      return conversations.filter(c => c.claimed_by === userProfile?.id);
    } else {
      // Available = unclaimed conversations that are not resolved/closed
      return conversations.filter(c => 
        c.claimed_by === null && 
        c.status !== 'resolved' && 
        c.status !== 'closed'
      );
    }
  }, [conversations, viewMode, userProfile?.id]);

  // Counts for UI
  const availableCount = useMemo(() => 
    conversations.filter(c => c.claimed_by === null).length,
  [conversations]);
  
  const myCount = useMemo(() => 
    conversations.filter(c => c.claimed_by === userProfile?.id).length,
  [conversations, userProfile?.id]);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback((immediate = false) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: immediate ? 'auto' : 'smooth',
        block: 'end'
      });
    }
  }, []);

  // Track scroll position
  const trackScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      handleMessageScroll(messagesContainerRef.current);
    }
  }, [handleMessageScroll]);

  // Show scroll button when not at bottom
  useEffect(() => {
    setShowScrollButton(!isAtBottom && messages.length > 0);
  }, [isAtBottom, messages.length]);

  // Auto-scroll when messages arrive
  useEffect(() => {
    if (messages.length > 0 && isAtBottom) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom, isAtBottom]);

  // Send message (auto-claims on first response)
  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !selectedConversation?.id || !userProfile?.id) return;
    
    try {
      setUiError(null);
      
      const result = await sendMessage(newMessage.trim(), userProfile.id, true);
      
      if (result.success) {
        setNewMessage('');
        
        // Reload conversations to get updated claim status
        setTimeout(() => {
          loadConversations();
        }, 500);
        
        scrollToBottom();
      } else {
        setUiError(result.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('[ModeratorChat] Send message error:', err);
      setUiError(err.message || 'Failed to send message');
    }
  }, [newMessage, selectedConversation, userProfile, sendMessage, scrollToBottom, loadConversations]);

  // Update conversation status (only for claimed conversations)
  const handleStatusChange = useCallback(async (conversationId, newStatus) => {
    if (!userProfile?.id) return;
    
    const conversation = conversations.find(c => c.id === conversationId);
    
    // Only allow if moderator claimed this conversation
    if (conversation?.claimed_by !== userProfile.id) {
      setUiError('You can only update conversations you have claimed');
      return;
    }
    
    try {
      const { error } = await supabase
        .from('chat_conversations')
        .update({ 
          status: newStatus, 
          updated_at: new Date().toISOString(),
          resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null
        })
        .eq('id', conversationId);
      
      if (error) throw error;
      
      // Update local state
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, status: newStatus } : c
      ));
      
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('[ModeratorChat] Status update error:', err);
      setUiError(err.message || 'Failed to update status');
    }
  }, [userProfile, conversations, selectedConversation]);

  // Check if moderator can interact with conversation
  const canInteract = useCallback((conversation) => {
    if (!conversation || !userProfile?.id) return false;
    // Can interact if unclaimed or claimed by this moderator
    return conversation.claimed_by === null || conversation.claimed_by === userProfile.id;
  }, [userProfile?.id]);

  // Real-time subscription for messages
  const handleMessageUpdate = useCallback(async () => {
    if (selectedConversation?.id) {
      console.log('[ModeratorChat] Real-time message received');
      await loadMessages();
    }
  }, [selectedConversation?.id, loadMessages]);

  useRealtimeSubscription(
    selectedConversation ? `moderator-messages:${selectedConversation.id}` : null,
    selectedConversation ? [{
      event: 'INSERT',
      schema: 'public',
      table: 'chat_messages',
      filter: `conversation_id=eq.${selectedConversation.id}`
    }] : [],
    handleMessageUpdate
  );

  // Real-time subscription for conversation updates
  const handleConversationUpdate = useCallback(async () => {
    await loadConversations();
  }, [loadConversations]);

  useRealtimeSubscription(
    'moderator-conversations',
    [{
      event: '*',
      schema: 'public',
      table: 'chat_conversations'
    }],
    handleConversationUpdate
  );

  // Handle escalation success
  const handleEscalationSuccess = useCallback((ticket) => {
    setShowEscalateModal(false);
    alert(`✅ Ticket ${ticket.ticket_number} created successfully!\n\nThis conversation has been escalated to a support ticket.`);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 pb-20 md:pb-8">
        <div className="content-container">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Moderator Chat</h1>
                <p className="text-text-secondary mt-1">
                  Respond to customer conversations. First response claims the conversation.
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <Icon name="Info" size={16} />
                <span>Claimed conversations are exclusively yours</span>
              </div>
            </div>

            {/* View Mode Tabs */}
            <div className="flex gap-2 border-b border-border">
              <button
                onClick={() => setViewMode('available')}
                className={`px-4 py-3 flex items-center gap-2 border-b-2 font-medium transition-colors ${
                  viewMode === 'available'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name="Inbox" size={18} />
                Available
                {availableCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                    {availableCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setViewMode('my')}
                className={`px-4 py-3 flex items-center gap-2 border-b-2 font-medium transition-colors ${
                  viewMode === 'my'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name="User" size={18} />
                My Conversations
                {myCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                    {myCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversations List */}
            <div className="lg:col-span-1 bg-white rounded-lg border border-border overflow-hidden flex flex-col h-[calc(100vh-16rem)]">
              <div className="p-4 border-b border-border bg-gray-50">
                <h2 className="font-semibold text-text-primary">
                  {viewMode === 'available' ? 'Available Conversations' : 'My Claimed Conversations'}
                </h2>
                <p className="text-xs text-text-muted mt-1">
                  {viewMode === 'available' 
                    ? 'Respond to claim a conversation' 
                    : 'Conversations you are handling'}
                </p>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </div>
                ) : error ? (
                  <div className="p-4 text-center text-red-600 text-sm">{error}</div>
                ) : filteredConversations.length === 0 ? (
                  <div className="p-8 text-center text-text-muted">
                    <Icon name={viewMode === 'available' ? 'Inbox' : 'MessageCircle'} size={40} className="mx-auto mb-2 opacity-30" />
                    <p>{viewMode === 'available' ? 'No available conversations' : 'No claimed conversations'}</p>
                  </div>
                ) : (
                  filteredConversations.map(conversation => (
                    <button
                      key={conversation.id}
                      onClick={() => setSelectedConversation(conversation)}
                      className={`w-full p-4 border-b border-border text-left hover:bg-gray-50 transition-colors ${
                        selectedConversation?.id === conversation.id ? 'bg-primary-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-medium text-text-primary text-sm truncate">
                            {conversation.user?.full_name || 'Unknown User'}
                          </h3>
                          {conversation.claimed_by && viewMode === 'available' && (
                            <span className="text-xs text-orange-600">
                              Claimed by {conversation.claimed_moderator?.full_name}
                            </span>
                          )}
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded border whitespace-nowrap ${statusColors[conversation.status]}`}>
                          {conversation.status}
                        </span>
                      </div>
                      <p className="text-xs text-text-muted mb-2 truncate">{conversation.subject}</p>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${priorityColors[conversation.priority]}`}>
                          {conversation.priority}
                        </span>
                        <span className="text-xs text-text-muted">
                          {new Date(conversation.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Chat Panel */}
            <div className="lg:col-span-2 bg-white rounded-lg border border-border flex flex-col h-[calc(100vh-16rem)] relative">
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-border">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="font-semibold text-text-primary">
                          {selectedConversation.user?.full_name}
                        </h2>
                        <p className="text-sm text-text-muted">{selectedConversation.subject}</p>
                        <p className="text-xs text-text-muted mt-1">
                          {selectedConversation.user?.email}
                        </p>
                        {selectedConversation.claimed_by === userProfile?.id && (
                          <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                            <Icon name="Lock" size={12} />
                            Claimed by you
                          </span>
                        )}
                        {selectedConversation.claimed_by && selectedConversation.claimed_by !== userProfile?.id && (
                          <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded">
                            <Icon name="AlertCircle" size={12} />
                            Claimed by another moderator
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        {/* Status dropdown - only if claimed by this moderator */}
                        {selectedConversation.claimed_by === userProfile?.id && (
                          <select
                            value={selectedConversation.status}
                            onChange={(e) => handleStatusChange(selectedConversation.id, e.target.value)}
                            className="px-3 py-2 border border-border rounded-lg text-sm"
                          >
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div 
                    ref={messagesContainerRef}
                    onScroll={trackScroll}
                    className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
                  >
                    {messagesError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                        {messagesError}
                      </div>
                    )}
                    
                    {messagesLoading && messages.length === 0 && (
                      <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    )}

                    {messages.length === 0 && !messagesLoading && (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <p>No messages yet</p>
                      </div>
                    )}

                    {messages.map(message => (
                      <div
                        key={message.id}
                        className={`flex ${message.is_admin ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs px-4 py-2 rounded-lg ${
                            message.is_admin
                              ? 'bg-primary text-white'
                              : 'bg-white text-text-primary border border-border'
                          }`}
                        >
                          <p className="text-sm">{message.message_text}</p>
                          <p className={`text-xs mt-1 ${message.is_admin ? 'text-primary-100' : 'text-text-muted'}`}>
                            {message.sender?.full_name} • {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Scroll to Bottom Button */}
                  {showScrollButton && messages.length > 0 && (
                    <button
                      onClick={() => scrollToBottom()}
                      className="absolute bottom-24 right-4 w-10 h-10 bg-primary/40 text-white rounded-full shadow-lg hover:bg-primary/60 transition-all flex items-center justify-center z-20"
                      title="Scroll to bottom"
                    >
                      <Icon name="ArrowDown" size={20} />
                    </button>
                  )}

                  {/* Input - only if can interact and not resolved/closed */}
                  {canInteract(selectedConversation) && 
                   selectedConversation.status !== 'closed' && 
                   selectedConversation.status !== 'resolved' ? (
                    <div className="p-4 border-t border-border space-y-3">
                      {uiError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                          {uiError}
                          <button onClick={() => setUiError(null)} className="ml-2 font-semibold">×</button>
                        </div>
                      )}
                      
                      {/* Actions - only for claimed conversations */}
                      {selectedConversation.claimed_by === userProfile?.id && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowEscalateModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
                          >
                            <Icon name="AlertCircle" size={16} />
                            Escalate to Ticket
                          </button>
                        </div>
                      )}
                      
                      {/* First response notice for unclaimed */}
                      {!selectedConversation.claimed_by && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex items-center gap-2">
                          <Icon name="Info" size={16} />
                          <span>Sending a message will claim this conversation exclusively for you.</span>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                          placeholder={selectedConversation.claimed_by ? "Type your message..." : "Send first message to claim..."}
                          className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          disabled={messagesLoading}
                        />
                        <Button
                          variant="primary"
                          onClick={handleSendMessage}
                          disabled={!newMessage.trim() || messagesLoading}
                        >
                          {messagesLoading ? 'Sending...' : selectedConversation.claimed_by ? 'Send' : 'Send & Claim'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border-t border-border bg-gray-50 text-center text-text-muted">
                      {selectedConversation.status === 'closed' || selectedConversation.status === 'resolved'
                        ? `This conversation is ${selectedConversation.status}. Messages cannot be sent.`
                        : 'This conversation is claimed by another moderator'}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <Icon name="MessageCircle" size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-text-muted">Select a conversation to start chatting</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Escalate to Ticket Modal */}
      <EscalateToTicketModal
        conversationId={selectedConversation?.id}
        isOpen={showEscalateModal}
        onClose={() => setShowEscalateModal(false)}
        onSuccess={handleEscalationSuccess}
      />
    </div>
  );
};

export default ModeratorChatDashboard;
